import React, { useEffect, useMemo, useState } from 'react';
import { getApiUrl } from '../config';
import { useNotification } from './Notification';
import { useResponsive } from '../hooks/useResponsive';
import { logger } from '../utils/logger';

type MatchResult = 'WIN' | 'LOSS' | 'DRAW' | undefined;

interface MatchHistoryItem {
  id: string;
  matchId: string;
  matchDate: string;
  location: string;
  teamName: string;
  position: string;
  goals: number;
  assists: number;
  saves: number;
  isMVP: boolean;
  isTopScorer: boolean;
  isTopAssister: boolean;
  isTopSaver: boolean;
  matchResult?: MatchResult;
  goalDifference?: number | null;
}

const RESULT_META: Record<'WIN' | 'DRAW' | 'LOSS', { label: string; color: string }> = {
  WIN: { label: 'Ganó', color: '#00E676' },
  DRAW: { label: 'Empató', color: '#FFD600' },
  LOSS: { label: 'Perdió', color: '#FF6B6B' },
};

function diffLabel(d?: number | null): string {
  if (d === null || d === undefined) return '';
  if (d > 0) return `+${d}`;
  if (d < 0) return `${d}`;
  return '=';
}

interface StatsHistoryProps {
  userId: string;
  isMobile?: boolean;
  useMockData?: boolean;
  mockMatches?: MatchHistoryItem[];
  isPublicProfile?: boolean;
  /** Perfil ajeno no amigo: oculta la ubicación / cancha en el listado por partido. */
  hideVenueDetails?: boolean;
}

type StatKey = 'goals' | 'assists' | 'saves';

const STAT_LABEL: Record<StatKey, string> = {
  goals: 'Goles',
  assists: 'Asistencias',
  saves: 'Atajadas'
};

export const StatsHistory: React.FC<StatsHistoryProps> = ({ userId, isMobile = false, useMockData = false, mockMatches, isPublicProfile = false, hideVenueDetails = false }) => {
  const { notification, showNotification, hideNotification } = useNotification();
  const { isMobile: isMobileHook } = useResponsive();
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState<MatchHistoryItem[]>([]);
  const [selected, setSelected] = useState<StatKey>('goals');

  const buildDefaultMock = (): MatchHistoryItem[] => {
    const now = new Date();
    const day = (offset: number) => new Date(now.getTime() - offset * 24 * 60 * 60 * 1000).toISOString();
    const base: Omit<MatchHistoryItem, 'id' | 'matchId' | 'matchDate'> = {
      location: 'Cancha Test',
      teamName: 'Equipo A',
      position: 'Mediocampista',
      goals: 0,
      assists: 0,
      saves: 0,
      isMVP: false,
      isTopScorer: false,
      isTopAssister: false,
      isTopSaver: false,
      matchResult: 'DRAW'
    };
    return [
      // Últimos 3 (más altos en goles)
      { id: 'm1', matchId: 'm1', matchDate: day(2), ...base, goals: 2, assists: 1, saves: 0, matchResult: 'WIN' },
      { id: 'm2', matchId: 'm2', matchDate: day(5), ...base, goals: 1, assists: 0, saves: 1, matchResult: 'DRAW' },
      { id: 'm3', matchId: 'm3', matchDate: day(8), ...base, goals: 0, assists: 2, saves: 0, matchResult: 'LOSS' },
      // Previos 3 (más bajos en goles)
      { id: 'm4', matchId: 'm4', matchDate: day(12), ...base, goals: 0, assists: 0, saves: 2, matchResult: 'DRAW' },
      { id: 'm5', matchId: 'm5', matchDate: day(16), ...base, goals: 0, assists: 1, saves: 3, matchResult: 'LOSS' },
      { id: 'm6', matchId: 'm6', matchDate: day(20), ...base, goals: 1, assists: 0, saves: 1, matchResult: 'WIN' }
    ];
  };

  const fetchLastMatches = async () => {
    try {
      setLoading(true);
      if (useMockData) {
        const data = (mockMatches && mockMatches.length ? mockMatches : buildDefaultMock()).sort((a, b) => new Date(b.matchDate).getTime() - new Date(a.matchDate).getTime());
        setMatches(data);
      } else {
        // Usar endpoint público o privado según el contexto
        const endpoint = isPublicProfile 
          ? `/api/users/${userId}/public-match-history?page=1&limit=6`
          : `/api/users/${userId}/match-history?page=1&limit=6`;
        
        const headers: HeadersInit = {};
        
        // Solo agregar token si no es perfil público
        if (!isPublicProfile) {
          const token = localStorage.getItem('authToken');
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
        }
        
        const response = await fetch(getApiUrl(endpoint), { headers });
        if (response.ok) {
          const data = await response.json();
          const history: MatchHistoryItem[] = (data.history || data.matches || []).sort((a: MatchHistoryItem, b: MatchHistoryItem) => new Date(b.matchDate).getTime() - new Date(a.matchDate).getTime());
          setMatches(history);
        } else {
          const error = await response.json();
          showNotification(error.error || 'Error cargando historial', 'error');
        }
      }
    } catch (err) {
      logger.error('Error fetching last matches', err);
      showNotification('Error de conexión', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchLastMatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const { last3, prev3, delta, percent, last3List, prev3List } = useMemo(() => {
    const values = matches.map(m => ({
      date: m.matchDate,
      goals: m.goals,
      assists: m.assists,
      saves: m.saves
    }));
    const last = values.slice(0, 3);
    const prev = values.slice(3, 6);

    const sum = (arr: typeof values, key: StatKey) => arr.reduce((acc, v) => acc + (v[key] || 0), 0);
    const l3 = sum(last as any, selected);
    const p3 = sum(prev as any, selected);
    const d = l3 - p3;
    const pct = p3 === 0 ? (l3 > 0 ? 100 : 0) : Math.round((d / p3) * 100);
    return {
      last3: l3,
      prev3: p3,
      delta: d,
      percent: pct,
      last3List: last.map(v => v[selected]),
      prev3List: prev.map(v => v[selected])
    };
  }, [matches, selected]);

  // Registro personal: partidos cargados a mano / importados (matchId o
  // ubicación los identifican). En ese caso mostramos resultado + diferencia de
  // goles en vez del comparador de goles/asistencias/atajadas.
  const isPersonalLog = useMemo(
    () =>
      matches.length > 0 &&
      matches.every(
        (m) => m.location === 'Registro personal' || /^(manual-|mifutbol-)/.test(m.matchId)
      ),
    [matches]
  );

  const colorForDelta = (d: number) => (d > 0 ? '#00E676' : d < 0 ? '#FF6B6B' : '#B0BEC5');

  const renderMiniBars = (values: number[]) => {
    if (!values.length) return <span style={{ color: '#B0BEC5', fontSize: isMobile ? '11px' : '12px' }}>Sin datos</span>;
    const max = Math.max(1, ...values);
    return (
      <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: isMobile ? 22 : 26 }}>
        {values.map((v, idx) => (
          <div key={idx} style={{ width: 10, height: Math.max(2, (v / max) * (isMobile ? 22 : 26)), backgroundColor: '#00E676', borderRadius: 3, opacity: 0.9 }} />
        ))}
      </div>
    );
  };

  return (
    <div style={{
      backgroundColor: '#23272A',
      padding: isMobile ? '14px' : '24px',
      borderRadius: '18px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
      border: '1px solid #33383B'
    }}>
      <h2 style={{ fontSize: isMobile ? '17px' : '22px', fontWeight: 800, margin: '0 0 12px 0', color: '#00E676' }}>
        📈 {isPersonalLog ? 'Historial de partidos' : 'Historial de Estadísticas'}
      </h2>

      {/* Selector de stat con estilo de tabs (solo si hay stats de juego) */}
      {!isPersonalLog && (
      <div style={{
        display: 'flex',
        gap: 0,
        marginBottom: 20,
        backgroundColor: '#2C2F33',
        borderRadius: 8,
        padding: 4,
        border: '1px solid #40444B'
      }}>
        {(['goals','assists','saves'] as StatKey[]).map((key) => (
          <button
            key={key}
            onClick={() => setSelected(key)}
            style={{
              flex: 1,
              padding: isMobile ? '8px 12px' : '10px 16px',
              backgroundColor: selected === key ? '#00E676' : 'transparent',
              color: selected === key ? '#181A1B' : '#FFFFFF',
              border: 'none',
              borderRadius: 6,
              fontSize: isMobile ? 12 : 14,
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {key === 'goals' ? '⚽ ' : key === 'assists' ? '➡️ ' : '🧤 '}{STAT_LABEL[key]}
          </button>
        ))}
      </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: isMobile ? 20 : 30, color: '#B0BEC5' }}>Cargando...</div>
      ) : (
        <div>
          {!isPersonalLog && (<>
          {/* Tarjeta resumen */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? 8 : 12,
            marginBottom: isMobile ? 10 : 14
          }}>
            <div style={{ border: '1px solid #40444B', borderRadius: 12, padding: isMobile ? 10 : 14, backgroundColor: '#2C2F33' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ color: '#B0BEC5', fontSize: isMobile ? 12 : 13 }}>Últimos 3</span>
                <span style={{ fontWeight: 800, fontSize: isMobile ? 16 : 18 }}>{last3}</span>
              </div>
              {renderMiniBars(last3List)}
            </div>

            <div style={{ border: '1px solid #40444B', borderRadius: 12, padding: isMobile ? 10 : 14, backgroundColor: '#2C2F33' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ color: '#B0BEC5', fontSize: isMobile ? 12 : 13 }}>Anteriores 3</span>
                <span style={{ fontWeight: 800, fontSize: isMobile ? 16 : 18 }}>{prev3}</span>
              </div>
              {renderMiniBars(prev3List)}
            </div>
          </div>

          {/* Delta */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: '1px solid #40444B',
            borderRadius: 12,
            padding: isMobile ? 10 : 14,
            backgroundColor: '#2C2F33',
            marginBottom: isMobile ? 10 : 14
          }}>
            <div style={{ color: '#FFFFFF', fontWeight: 700, fontSize: isMobile ? 14 : 16 }}>
              Variación ({STAT_LABEL[selected]})
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: colorForDelta(delta), fontWeight: 800, fontSize: isMobile ? 16 : 18 }}>
                {delta > 0 ? '▲' : delta < 0 ? '▼' : '•'} {delta}
              </span>
              <span style={{ color: '#B0BEC5', fontSize: isMobile ? 12 : 13 }}>({percent}%)</span>
            </div>
          </div>
          </>)}

          {/* Lista compacta de los partidos */}
          <div>
            <h3 style={{ margin: '0 0 8px 0', color: '#FFFFFF', fontSize: isMobile ? 14 : 16 }}>
              {isPersonalLog ? 'Partidos recientes' : 'Partidos considerados'}
            </h3>
            {(matches || []).slice(0, 6).map((m, idx) => {
              const meta = m.matchResult ? RESULT_META[m.matchResult] : null;
              return (
              <div key={m.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: isMobile ? '8px 10px' : '10px 12px',
                borderRadius: 10,
                border: '1px solid #40444B',
                backgroundColor: '#2C2F33',
                marginBottom: 6
              }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ color: '#B0BEC5', fontSize: isMobile ? 12 : 13 }}>
                    {new Date(m.matchDate).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}
                    {isPersonalLog ? (
                      meta && (
                        <span style={{ marginLeft: 8, color: meta.color, fontWeight: 700 }}>
                          {meta.label}
                          {m.goalDifference !== null && m.goalDifference !== undefined && (
                            <span style={{ marginLeft: 6 }}>{diffLabel(m.goalDifference)}</span>
                          )}
                        </span>
                      )
                    ) : (
                      <span style={{ marginLeft: 8, color: '#FFFFFF' }}>
                        {selected === 'goals' ? `⚽ ${m.goals}` : selected === 'assists' ? `➡️⚽ ${m.assists}` : `🧤⚽ ${m.saves}`}
                      </span>
                    )}
                  </div>
                  {!isPersonalLog && (
                    <div style={{ color: '#8FA3AD', fontSize: isMobile ? 11 : 12 }}>
                      📍 {hideVenueDetails ? 'Ubicación oculta' : m.location} • {m.teamName}
                    </div>
                  )}
                </div>
                {!isPersonalLog && <div style={{ fontSize: 12, color: '#B0BEC5' }}>{idx < 3 ? 'Últimos' : 'Previos'}</div>}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Notificación */}
      {notification.isVisible && (
        <button type="button" onClick={hideNotification} style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '16px 20px',
          borderRadius: '8px',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '14px',
          zIndex: 1001,
          maxWidth: '400px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: 'pointer',
          border: 'none',
          backgroundColor: '#00E676'
        }}>
          <span>ℹ️</span>
          <span>{notification.message}</span>
        </button>
      )}
    </div>
  );
};

export default StatsHistory;


